import {useReferentialContext} from "../../Context";
import {
    Alert,
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, 
    Grid,
    Snackbar,
    Stack,
    Typography,
    Divider,
    Chip
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {ClientOrganization, User} from "../../reckon-api";
import {GridCellEditStopParams, GridColDef} from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import TeamIcon from "../../components/Icons/TeamIcon";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {NeonButton} from "../../NeonComponents/Buttons";
import {NeonCard, NeonCardHeader, NeonCardContent} from "../../NeonComponents/NeonCards";
import AddIcon from "@mui/icons-material/Add";
import KeyIcon from "@mui/icons-material/Key";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import {scrollable} from "../../theme";
import StyledDatagrid, { ColumnTypes } from "../../components/datagrid/styled_datagrid";


export default function ManageOrg(){
    const [selectedTeam, setSelectedTeam] = useState<string | null>(null);

    return (
        <Box 
            sx={{
                width: '100%',
                height: '100%',
                p: { xs: 1, sm: 2, md: 3 },
                ...scrollable,
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 2, md: 3 }
            }}
        >
            <Grid 
                container 
                spacing={{ xs: 2, md: 3 }}
                sx={{
                    width: '100%',
                    height: '100%',
                    flex: 1,
                    minHeight: 0
                }}
            >
                {/* Users Manager - Responsive: Full width on mobile, smaller on desktop */}
                <Grid 
                    size={{ xs: 12, md: 5, lg: 4.5 }} 
                    sx={{
                        height: { xs: 'auto', md: '100%' },
                        minHeight: { xs: '500px', md: '600px' },
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <UsersManager selectedTeam={selectedTeam} setSelectedTeam={setSelectedTeam}/>
                </Grid>
                
                {/* Organization Manager - Responsive: Full width on mobile, larger on desktop */}
                <Grid 
                    size={{ xs: 12, md: 7, lg: 7.5 }} 
                    sx={{
                        height: { xs: 'auto', md: '100%' },
                        minHeight: { xs: '500px', md: '600px' },
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <ClientOrganizationManager selectedTeam={selectedTeam} setSelectedTeam={setSelectedTeam}/>
                </Grid>
            </Grid>
        </Box>
    );
}

function ClientOrganizationManager(props: { selectedTeam: string | null; setSelectedTeam: (team: string | null) => void }){
    const ctx = useReferentialContext();
    const [organization, setOrg] = useState(null as null|ClientOrganization);
    const [grid_key, set_grid_key] = useState(organization?.grid_api_key);
    const [isValidating, setIsValidating] = useState(false);

    const [toast, setToast] = useState({
        open: false,
        message: "",
        severity: "success" as "success" | "error" | "warning" | "info",
    });

    useEffect(() => {
        ctx.reckonApi.clientOrganization.get({id: ctx.user.organization}).then(org => {
            setOrg(org);
            set_grid_key(org.grid_api_key)
        })
    }, []);
    
    const handleToastClose = () => setToast((prevToast: any) => ({ ...prevToast, open: false }));

    const handleValidateGridKey = () => {
        if(!grid_key) {
            setToast({
                open: true,
                message: "Please enter a Grid API key",
                severity: "error",
            });
            return;
        }
        
        setIsValidating(true);
        fetch('https://api.grid.gg/central-data/graphql', {
            method: 'POST', 
            body: JSON.stringify({
                "operationName":"GetHistoricalSeries",
                "variables":{"first":10,"windowStartTime":"2022-01-01T00:00:00.000+01:00","windowEndTime":"2025-07-01T23:59:59.999+02:00","titleIds":["3"]},
                "query":"query GetHistoricalSeries($windowStartTime: String!, $windowEndTime: String!, $after: Cursor, $before: Cursor, $first: Int, $last: Int, $livePlayerIds: [ID!], $teamIds: [ID!], $tournamentIds: [ID!], $types: [SeriesType!], $titleIds: [ID!], $tournamentIncludeChildren: Boolean) {\n  allSeries(\n    filter: {startTimeScheduled: {gte: $windowStartTime, lte: $windowEndTime}, livePlayerIds: {in: $livePlayerIds}, tournament: {id: {in: $tournamentIds}, includeChildren: {equals: $tournamentIncludeChildren}}, teamIds: {in: $teamIds}, types: $types, titleIds: {in: $titleIds}}\n    first: $first\n    last: $last\n    after: $after\n    before: $before\n    orderBy: StartTimeScheduled\n    orderDirection: DESC\n  ) {\n    totalCount\n    pageInfo {\n      hasPreviousPage\n      hasNextPage\n      startCursor\n      endCursor\n      __typename\n    }\n    edges {\n      node {\n        id\n        type\n        title {\n          name\n          nameShortened\n          __typename\n        }\n        tournament {\n          name\n          __typename\n        }\n        startTimeScheduled\n        format {\n          nameShortened\n          __typename\n        }\n        teams {\n          baseInfo {\n            name\n            logoUrl\n            id\n            __typename\n          }\n          __typename\n        }\n        productServiceLevels {\n          productName\n          serviceLevel\n          __typename\n        }\n        externalLinks {\n          dataProvider {\n            name\n            __typename\n          }\n          externalEntity {\n            id\n            __typename\n          }\n          __typename\n        }\n        __typename\n      }\n      __typename\n    }\n    __typename\n  }\n}\n"
            }), 
            headers: {"x-api-key": grid_key, 'Content-Type': 'application/json'}
        })
        .then(res => res.json())
        .then(res => {
            setIsValidating(false);
            if(!res.errors) {
                ctx.reckonApi.clientOrganization.patch({
                    id: organization!.id, 
                    patched_client_organization: {grid_api_key: grid_key}
                }).then(r => {
                    setToast({
                        open: true,
                        message: `Grid API key validated successfully. We will now start to update your scrims.`,
                        severity: "success",
                    });
                });
            } else {
                setToast({
                    open: true,
                    message: `Grid API key is not valid. Please check your key and try again.`,
                    severity: "error",
                });
            }
        })
        .catch(error => {
            setIsValidating(false);
            setToast({
                open: true,
                message: `Error validating API key: ${error.message}`,
                severity: "error",
            });
        });
    };

    if (!organization) {
        return (
            <NeonCard sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <NeonCardContent>
                    <Typography variant="h5" sx={{ textAlign: 'center' }}>Loading organization...</Typography>
                </NeonCardContent>
            </NeonCard>
        );
    }

    return (
        <Stack direction={'column'} spacing={2} sx={{ height: '100%', width: '100%' }}>
            {/* Organization Header Card */}
            <NeonCard>
                <NeonCardHeader 
                    title={
                        <Stack direction={'row'} spacing={2} alignItems="center">
                            <BusinessIcon />
                            <Typography variant="h5" component="span">Organization Details</Typography>
                        </Stack>
                    }
                />
                <NeonCardContent>
                    <Stack direction={'column'} spacing={2}>
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            spacing={2} 
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                        >
                            <TeamIcon team_name={organization.id}/>
                            <Box>
                                <Typography variant={'h5'} sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    {organization.id}
                                </Typography>
                                <Stack direction={'row'} spacing={1} alignItems="center" flexWrap="wrap">
                                    <Chip 
                                        label={organization.plan} 
                                        color="primary" 
                                        size="small"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                    <Typography variant={'body2'} color="text.secondary">
                                        Subscribed until {organization.subscription_ends}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>
                    </Stack>
                </NeonCardContent>
            </NeonCard>

            {/* Grid API Key Card */}
            <NeonCard>
                <NeonCardHeader 
                    title={
                        <Stack direction={'row'} spacing={2} alignItems="center">
                            <KeyIcon />
                            <Typography variant="h5" component="span">Grid API Key</Typography>
                        </Stack>
                    }
                />
                <NeonCardContent>
                    <Stack direction={'column'} spacing={2}>
                        <Typography variant={'body2'} color="text.secondary">
                            Enter your Grid API key to enable automatic scrim updates
                        </Typography>
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            spacing={2} 
                            sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
                        >
                            <TextField
                                sx={{ 
                                    flex: { xs: 1, sm: '0 1 auto' },
                                    maxWidth: { xs: '100%', sm: '400px' }
                                }}
                                label="Grid API Key"
                                value={grid_key || ''}
                                onChange={(e) => set_grid_key(e.target.value)}
                                variant="outlined"
                                type="password"
                                placeholder="Enter your Grid API key"
                                disabled={isValidating}
                            />
                            <NeonButton 
                                variant='outlined' 
                                onClick={handleValidateGridKey}
                                disabled={isValidating || !grid_key}
                                sx={{ 
                                    minWidth: { xs: '100%', sm: '120px' },
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}
                            >
                                {isValidating ? 'Validating...' : 'Validate'}
                            </NeonButton>
                        </Stack>
                    </Stack>
                </NeonCardContent>
            </NeonCard>

            {/* Teams Card */}
            <NeonCard sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <NeonCardHeader 
                    title={
                        <Stack direction={'row'} spacing={2} alignItems="center">
                            <PeopleIcon />
                            <Typography variant="h5" component="span">Teams in Organization</Typography>
                        </Stack>
                    }
                />
                <NeonCardContent sx={{ flex: 1, ...scrollable, minHeight: 0, maxHeight: '100%' }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                            gap: 2
                        }}
                    >
                        {organization.teams.split(',').map((t, index) => {
                            const teamName = t.trim();
                            const isSelected = props.selectedTeam === teamName;
                            
                            return (
                                <Box
                                    key={index}
                                    onClick={() => {
                                        // Toggle: if clicking the same team, clear filter; otherwise set it
                                        props.setSelectedTeam(isSelected ? null : teamName);
                                    }}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        border: '2px solid',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        backgroundColor: isSelected ? 'action.selected' : 'transparent',
                                        minHeight: '60px',
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            backgroundColor: isSelected ? 'action.selected' : 'action.hover',
                                            borderColor: isSelected ? 'primary.main' : 'primary.light',
                                            transform: 'translateY(-2px)',
                                            boxShadow: isSelected 
                                                ? '0 4px 12px rgba(0, 0, 0, 0.15)' 
                                                : '0 2px 8px rgba(0, 0, 0, 0.1)'
                                        }
                                    }}
                                >
                                    <Box sx={{ flexShrink: 0 }}>
                                        <TeamIcon team_name={teamName}/>
                                    </Box>
                                    <Typography 
                                        variant={'h6'} 
                                        sx={{ 
                                            fontWeight: 'bold', 
                                            flex: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            minWidth: 0,
                                            color: isSelected ? 'primary.main' : 'text.primary'
                                        }}
                                    >
                                        {teamName}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </NeonCardContent>
            </NeonCard>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={handleToastClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={handleToastClose} severity={toast.severity} variant="filled">
                    {toast.message}
                </Alert>
            </Snackbar>
        </Stack>
    );
}

function UsersManager(props: { selectedTeam: string | null; setSelectedTeam: (team: string | null) => void }){
    const ctx = useReferentialContext();
    const [users, setUsers] = useState([] as User[]);
    const [isLoading, setLoading] = useState(false);
    const [createUserOpen, setCreateUserOpen] = useState(false);

    const [organization, setOrg] = useState(null as null|ClientOrganization);
    
    // Filter users based on selected team
    const filteredUsers = props.selectedTeam 
        ? users.filter(user => user.team === props.selectedTeam)
        : users;

    useEffect(() => {
        ctx.reckonApi.clientOrganization.get({id: ctx.user.organization}).then(org => setOrg(org))
    }, []);
    const reload = () => {
        setLoading(true)
        ctx.reckonApi.users.list({ordering:['team','-is_staff']}).then(r => {
            setUsers(r);
            setLoading(false);
        })
    }

    useEffect(() => {
        reload()
    }, []);

    const grid_columns = [
        ColumnTypes.string("username", "Username", {
            minWidth: 120,
            maxWidth: 200,
            flex: 1
        }),
        {
            headerName: "Team",
            field: "team",
            align: "center",
            minWidth: 150,
            maxWidth: 250,
            headerAlign: "center",
            flex: 1,
            editable: true,
        } as GridColDef,
        {
            headerName: "Staff",
            field: "is_staff",
            align: "center",
            headerAlign: "center",
            minWidth: 100,
            maxWidth: 120,
            flex: 0,
            renderCell: (params: any) => (
                <Checkbox
                    checked={params.value}
                    onClick={() => {
                        ctx.reckonApi.users.patch({
                            id: params.row.id,
                            patched_user : {
                                is_staff: !params.value
                            }
                        }).then(() => reload())
                    }}
                />
            ),
        } as GridColDef,
        {
            headerName: "Delete",
            field: "id",
            align: "center",
            headerAlign: "center",
            minWidth: 90,
            maxWidth: 110,
            flex: 0,
            renderCell: (params: any) => (
                <Button
                    onClick={() => {
                        if (window.confirm(`Are you sure you want to delete user "${params.row.username}"?`)) {
                            ctx.reckonApi.users.delete({
                                id: params.row.id
                            }).then(() => reload())
                        }
                    }}
                    color="error"
                    size="small"
                    sx={{ minWidth: 'auto' }}
                >
                    <DeleteIcon />
                </Button>
            ),
        } as GridColDef,
    ]

    return (
        <NeonCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <NeonCardHeader 
                title={
                    <Stack direction={'row'} spacing={2} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Stack direction={'row'} spacing={2} alignItems="center">
                            <PeopleIcon />
                            <Typography variant="h5" component="span">User Management</Typography>
                        </Stack>
                        <NeonButton 
                            onClick={() => setCreateUserOpen(true)} 
                            variant={'outlined'}
                            startIcon={<AddIcon />}
                            sx={{ 
                                minWidth: { xs: 'auto', sm: '140px' },
                                whiteSpace: { xs: 'nowrap', sm: 'normal' }
                            }}
                        >
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                Create User
                            </Box>
                            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                Add
                            </Box>
                        </NeonButton>
                    </Stack>
                }
            />
            <NeonCardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: { xs: 1, sm: 2 } }}>
                {organization && <CreateUserDialog open={createUserOpen} setOpen={setCreateUserOpen} org={organization}/>}
                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                    <StyledDatagrid
                        autoPageSize
                        rowHeight={65}
                        sx={{
                            width: "100%",
                            height: "100%",
                            "& .header": {
                                width: "100%",
                                display: "flex",
                                alignItems: "flex-end",
                                justifyContent: "flex-end",
                            },
                            "& .border-left": {
                                borderLeft: "solid grey",
                                borderWidth: "2px",
                            },
                        }}
                        getRowId={(row) => row.id}
                        initialState={{
                            sorting: {
                                sortModel: [
                                    { field: "team", sort: "desc" },
                                    { field: "is_staff", sort: "desc" }
                                ],
                            },
                        }}
                        loading={isLoading}
                        columns={grid_columns}
                        rows={filteredUsers}
                        hideFooter={false}
                        onCellEditStop={(params: GridCellEditStopParams, event: any) => {
                            const request_params = {
                                id: params.row.id,
                                patched_user : {}
                            } as any;
                            request_params.patched_user[params.field] = event.target.value;
                            if(!request_params.patched_user.team || organization?.teams.includes(request_params.patched_user.team)) {
                             ctx.reckonApi.users.patch(request_params).then(() => reload())
                            }
                        }}
                    />
                </Box>
            </NeonCardContent>
        </NeonCard>
    );
}

function CreateUserDialog(props: {open: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>>, org: ClientOrganization}) {
  const ctx = useReferentialContext();
  const [formData, setFormData] = useState({
    username: "",
    team: props.org.teams.split(',')[0],
    is_staff: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Snackbar state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });

  const handleClose = () => {
    if (!isSubmitting) {
      props.setOpen(false);
      // Reset form when closing
      setFormData({
        username: "",
        team: props.org.teams.split(',')[0],
        is_staff: false,
      });
    }
  };

  // Handle form changes
  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Submit form
  const handleSubmit = async () => {
    if (!formData.username.trim()) {
      setToast({
        open: true,
        message: "Please enter a username",
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);
    ctx.reckonApi.users.create({id: 'random', user: formData as User}).then(
      r => {
        setIsSubmitting(false);
        handleClose();
        
        // Copy credentials to clipboard
        const credentials = `https://reckon-bolt.com\nUsername : ${formData.username}\nPassword : ${r.password}`;
        navigator.clipboard.writeText(credentials);
        
        setToast({
          open: true,
          message: `User "${formData.username}" created successfully. Credentials copied to clipboard!`,
          severity: "success",
        });
      }
    ).catch(error => {
      setIsSubmitting(false);
      setToast({
        open: true,
        message: `Error creating user: ${error.message}`,
        severity: "error",
      });
    });
  };

  // Close Snackbar
  const handleToastClose = () => setToast((prevToast: any) => ({ ...prevToast, open: false }));

  return (
    <>
      <Dialog 
        open={props.open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <AddIcon />
            <Typography variant="h6" component="span">Create New User</Typography>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              fullWidth
              required
              margin="normal"
              variant="outlined"
              disabled={isSubmitting}
              helperText="Enter a unique username for the new user"
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Team
              </Typography>
              <Select 
                fullWidth 
                name="team" 
                onChange={handleInputChange} 
                value={formData.team}
                disabled={isSubmitting}
                variant="outlined"
              >
                {props.org.teams.split(',').map((t, index) => (
                  <MenuItem key={index} value={t.trim()}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TeamIcon team_name={t.trim()} />
                      <Typography>{t.trim()}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  name="is_staff"
                  checked={formData.is_staff}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      is_staff: e.target.checked,
                    }))
                  }
                  disabled={isSubmitting}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography>Grant staff privileges</Typography>
                  <Chip label="Admin" size="small" color="primary" variant="outlined" />
                </Stack>
              }
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClose} 
            color="secondary"
            disabled={isSubmitting}
            sx={{ minWidth: { xs: '80px', sm: '100px' } }}
          >
            Cancel
          </Button>
          <NeonButton 
            onClick={handleSubmit} 
            variant="contained"
            disabled={isSubmitting || !formData.username.trim()}
            sx={{ minWidth: { xs: '100px', sm: '120px' } }}
          >
            {isSubmitting ? 'Creating...' : 'Create User'}
          </NeonButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={8000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}